import { useCallback, useEffect, useRef, useState } from 'react';

import type { PortalHubClientEvents, PortalHubConnection } from '@/signalr';

import { PORTAL_HUB_EVENT_NAMES, appendRecordedEvent, toRecordedEvent } from './portalHubEventLog';
import type { RecordedEventSource, RecordedHubEvent } from './portalHubEventLog';

const FLUSH_INTERVAL_MS = 150;

export const EVENT_LOG_CAPACITIES = [100, 250, 500, 1000] as const;

export const DEFAULT_EVENT_LOG_CAPACITY = 250;

export type DevPortalHubLog = {
  readonly events: readonly RecordedHubEvent[];
  readonly countsByName: ReadonlyMap<string, number>;
  readonly received: number;
  readonly isRecording: boolean;
  readonly capacity: number;
  readonly setRecording: (recording: boolean) => void;
  readonly setCapacity: (capacity: number) => void;
  readonly clear: () => void;
};

type LogSnapshot = {
  readonly events: readonly RecordedHubEvent[];
  readonly countsByName: ReadonlyMap<string, number>;
  readonly received: number;
};

const EMPTY: LogSnapshot = { events: [], countsByName: new Map(), received: 0 };

type RecordEvent = (source: RecordedEventSource, name: string, args: readonly unknown[]) => void;

function attachHubEvent<TEventName extends keyof PortalHubClientEvents & string>(
  hub: PortalHubConnection,
  eventName: TEventName,
  record: RecordEvent,
): () => void {
  const handler = (...args: unknown[]): void => record('hub', eventName, args);
  return hub.on(eventName, handler as unknown as PortalHubClientEvents[TEventName]);
}

export function useDevPortalHubLog(hub: PortalHubConnection | null): DevPortalHubLog {
  const [snapshot, setSnapshot] = useState<LogSnapshot>(EMPTY);
  const [isRecording, setIsRecording] = useState(true);
  const [capacity, setCapacityState] = useState<number>(DEFAULT_EVENT_LOG_CAPACITY);

  const eventsRef = useRef<readonly RecordedHubEvent[]>([]);
  const countsRef = useRef(new Map<string, number>());
  const receivedRef = useRef(0);
  const sequenceRef = useRef(0);
  const flushTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const recordingRef = useRef(isRecording);
  const capacityRef = useRef(capacity);

  const publish = useCallback(() => {
    setSnapshot({
      events: eventsRef.current,
      countsByName: new Map(countsRef.current),
      received: receivedRef.current,
    });
  }, []);

  const record = useCallback<RecordEvent>(
    (source, name, args) => {
      if (!recordingRef.current) {
        return;
      }

      sequenceRef.current += 1;
      receivedRef.current += 1;
      countsRef.current.set(name, (countsRef.current.get(name) ?? 0) + 1);
      eventsRef.current = appendRecordedEvent(
        eventsRef.current,
        toRecordedEvent(sequenceRef.current, source, name, args, new Date()),
        capacityRef.current,
      );

      flushTimerRef.current ??= setTimeout(() => {
        flushTimerRef.current = undefined;
        publish();
      }, FLUSH_INTERVAL_MS);
    },
    [publish],
  );

  useEffect(() => {
    if (hub === null) {
      return;
    }

    const detach = [
      ...PORTAL_HUB_EVENT_NAMES.map((eventName) => attachHubEvent(hub, eventName, record)),
      hub.onClose((error) => record('connection', 'HubClosed', [error ?? null])),
    ];

    return () => detach.forEach((remove) => remove());
  }, [hub, record]);

  useEffect(
    () => () => {
      clearTimeout(flushTimerRef.current);
      flushTimerRef.current = undefined;
    },
    [],
  );

  const setRecording = useCallback((recording: boolean) => {
    recordingRef.current = recording;
    setIsRecording(recording);
  }, []);

  const setCapacity = useCallback(
    (next: number) => {
      capacityRef.current = next;
      eventsRef.current = eventsRef.current.slice(0, next);
      setCapacityState(next);
      publish();
    },
    [publish],
  );

  const clear = useCallback(() => {
    eventsRef.current = [];
    countsRef.current = new Map();
    receivedRef.current = 0;
    publish();
  }, [publish]);

  return { ...snapshot, isRecording, capacity, setRecording, setCapacity, clear };
}
